import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { omapApi } from '@services/omapApi'

export interface Company {
  id: string
  name: string
  industry?: string
  description?: string
  naics_code?: string
  industry_label?: string
  ivp_status?: string
  data_readiness_tier?: string
  isSample?: boolean
  sampleSlug?: string
  isPublic?: boolean
  createdAt: string
  updatedAt: string
}

interface CompaniesState {
  companies: Company[]
  activeCompanyId: string | null
  loading: boolean
}

const initialState: CompaniesState = {
  companies: [],
  activeCompanyId: null,
  loading: false,
}

// Hard-delete a company on the backend (cascades across ~49 tables + visuals).
// On success, the reducer strips it from state and switches active company if
// the deleted one was active (preferring a non-sample owned company, falling
// back to the sample, then null).
export const deleteCompanyAsync = createAsyncThunk<
  string,  // returns the frontend id (e.g. "company_6") on success
  string,  // payload: frontend id
  { rejectValue: string }
>(
  'companies/deleteCompanyAsync',
  async (frontendId, { rejectWithValue }) => {
    const numericId = parseInt(frontendId.replace('company_', ''), 10)
    if (Number.isNaN(numericId)) {
      return rejectWithValue(`Invalid company id: ${frontendId}`)
    }
    try {
      await omapApi.deleteCompany(numericId)
      return frontendId
    } catch (error: any) {
      const detail = error?.response?.data?.detail
      const msg =
        (typeof detail === 'string' && detail) ||
        detail?.message ||
        error?.message ||
        'Failed to delete company'
      return rejectWithValue(msg)
    }
  }
)

const companiesSlice = createSlice({
  name: 'companies',
  initialState,
  reducers: {
    createCompany: (state, action: PayloadAction<{ name: string; industry?: string; description?: string }>) => {
      // Generate sequential company ID (company_1, company_2, ...)
      const nextId = state.companies.length > 0
        ? Math.max(...state.companies.map(c => parseInt(c.id.replace('company_', '')) || 0)) + 1
        : 1
      const companyId = `company_${nextId}`

      const newCompany: Company = {
        id: companyId,
        name: action.payload.name,
        industry: action.payload.industry,
        description: action.payload.description,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      state.companies.push(newCompany)
      state.activeCompanyId = newCompany.id
    },

    setActiveCompany: (state, action: PayloadAction<string>) => {
      state.activeCompanyId = action.payload
    },

    updateCompany: (state, action: PayloadAction<{ id: string; name?: string; industry?: string; description?: string }>) => {
      const company = state.companies.find(c => c.id === action.payload.id)
      if (company) {
        if (action.payload.name) company.name = action.payload.name
        if (action.payload.industry !== undefined) company.industry = action.payload.industry
        if (action.payload.description !== undefined) company.description = action.payload.description
        company.updatedAt = new Date().toISOString()
      }
    },

    createCompanyFromBackend: (state, action: PayloadAction<{
      id: string; name: string; industry?: string; naics_code?: string;
      ivp_status?: string; data_readiness_tier?: string;
      is_sample?: boolean; sample_slug?: string; is_public?: boolean
    }>) => {
      const p = action.payload
      const newCompany: Company = {
        id: p.id,
        name: p.name,
        industry: p.industry,
        naics_code: p.naics_code,
        ivp_status: p.ivp_status,
        data_readiness_tier: p.data_readiness_tier,
        isSample: !!p.is_sample,
        sampleSlug: p.sample_slug,
        isPublic: !!p.is_public,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      state.companies.push(newCompany)
      state.activeCompanyId = newCompany.id
    },

    deleteCompany: (state, action: PayloadAction<string>) => {
      state.companies = state.companies.filter(c => c.id !== action.payload)
      if (state.activeCompanyId === action.payload) {
        state.activeCompanyId = state.companies[0]?.id || null
      }
    },

    setCompanies: (state, action: PayloadAction<Company[]>) => {
      state.companies = action.payload
    },

    // Maps a backend company list (snake_case) to the slice shape (camelCase).
    // Called wherever /portfolio/{user_id}/companies is fetched (e.g. CompanySelector
    // health-score fetch, or any future loadCompanies thunk).
    setCompaniesFromBackend: (state, action: PayloadAction<any[]>) => {
      state.companies = action.payload.map((b: any) => ({
        id: typeof b.id === 'string' ? b.id : `company_${b.id}`,
        name: b.name,
        industry: b.industry,
        naics_code: b.naics_code,
        ivp_status: b.ivp_status,
        data_readiness_tier: b.data_readiness_tier,
        isSample: !!b.is_sample,
        sampleSlug: b.sample_slug,
        isPublic: !!b.is_public,
        createdAt: b.created_at || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })) as any
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteCompanyAsync.pending, (state) => {
        state.loading = true
      })
      .addCase(deleteCompanyAsync.fulfilled, (state, action) => {
        state.loading = false
        const deletedId = action.payload
        state.companies = state.companies.filter(c => c.id !== deletedId)
        if (state.activeCompanyId === deletedId) {
          // Prefer a non-sample owned company; fall back to any remaining; then null.
          const ownedNonSample = state.companies.find(c => !c.isSample)
          state.activeCompanyId =
            ownedNonSample?.id ?? state.companies[0]?.id ?? null
        }
      })
      .addCase(deleteCompanyAsync.rejected, (state) => {
        state.loading = false
      })
  },
})

export const {
  createCompany,
  createCompanyFromBackend,
  setActiveCompany,
  updateCompany,
  deleteCompany,
  setCompanies,
  setCompaniesFromBackend,
  setLoading,
} = companiesSlice.actions

export default companiesSlice.reducer
